import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { isMsalEnabled } from '../lib/auth.js';
import { useCurrentUser } from '../hooks/useCurrentUser.js';
import { useWindowWidth } from '../hooks/useWindowWidth.js';
import { useImpersonation } from '../hooks/useImpersonation.js';
import { usePreview } from '../hooks/usePreview.js';
import { useTheme } from '../hooks/useTheme.js';
import { useCapabilities, hasCapability } from '../hooks/useCapabilities.js';
import api from '../services/api.js';
import NotificationBell from './NotificationBell.jsx';
import HelpDrawer from './HelpDrawer.jsx';
import FeedbackButton from './FeedbackButton.jsx';
import FeedbackModal from './FeedbackModal.jsx';
import { HELP_MODULE_MAP, NAV_SECTIONS, ROLE_SECTION_VISIBILITY, ADMIN_GROUP, ING_SECTION, CUSTOMER_PORTAL_NAV, VENDOR_PORTAL_NAV, TAB_SEARCH_ENTRIES, PRIMARY_TAB_PATHS, initOpenState } from '../config/navConfig.jsx';
import { DashIcon, QualIcon, ReceivingIcon, DocsIcon, MoreDotsIcon, HamburgerIcon } from './layout/NavIcons.jsx';

// ── Content ErrorBoundary — catches page crashes, keeps sidebar alive ─────────
class ContentErrorBoundary extends React.Component {
  state = { error: null };
  static getDerivedStateFromError(e) { return { error: e }; }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-label)' }}>
        <h2 style={{ color: 'var(--color-danger)', marginBottom: '0.5rem' }}>Something went wrong</h2>
        <p style={{ color: 'var(--color-text-muted)', marginBottom: '1.5rem' }}>
          {import.meta.env.DEV ? String(this.state.error?.message) : 'Reload the page or contact support.'}
        </p>
        <button
          onClick={() => this.setState({ error: null })}
          style={{ padding: '0.5rem 1.5rem', background: 'var(--color-accent)', color: 'var(--color-accent-btn-text)', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
        >
          Try again
        </button>
      </div>
    );
  }
}

function ImpersonationTimer({ startedAt, onExpire }) {
  const expiresAt   = startedAt + 30 * 60 * 1000;
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  const [remaining, setRemaining] = useState(() =>
    Math.max(0, Math.round((expiresAt - Date.now()) / 1000))
  );

  useEffect(() => {
    const tick = () => {
      const secs = Math.max(0, Math.round((expiresAt - Date.now()) / 1000));
      setRemaining(secs);
      if (secs <= 0) onExpireRef.current('timeout');
    };
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const mins = String(Math.floor(remaining / 60)).padStart(2, '0');
  const secs = String(remaining % 60).padStart(2, '0');
  return <span>{mins}:{secs}</span>;
}

/* ── MobileTab ───────────────────────────────────────────────────────────── */

function MobileTab({ to, icon, label }) {
  return (
    <NavLink
      to={to}
      style={({ isActive }) => ({
        ...mobileTabBtn,
        color: isActive ? 'var(--color-accent)' : 'rgba(248,243,236,0.6)',
      })}
    >
      {icon}
      <span style={tabLabelStyle}>{label}</span>
    </NavLink>
  );
}

/* ── Layout ──────────────────────────────────────────────────────────────── */

export default function Layout() {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const user    = accounts?.[0];
  const { role, Name: dbName, authState } = useCurrentUser();

  function handleSignOut() {
    if (!isMsalEnabled) {
      sessionStorage.removeItem('magicSession');
      window.location.replace('/');
      return;
    }
    instance.logoutRedirect();
  }
  const isAdmin = role === 'admin';
  // ZEBRA-AUDIT-FIX-2 hole 5: a link may name an explicit role list; otherwise adminOnly rules.
  const roleOk = (l) => (Array.isArray(l.roles) ? l.roles.includes(role) : (!l.adminOnly || isAdmin));

  const _tenantCfg   = JSON.parse(sessionStorage.getItem('tenantConfig') || '{}');
  const brandName    = _tenantCfg.brandName    || _tenantCfg.companyName  || 'Littoral™';
  const brandTagline = _tenantCfg.brandTagline || _tenantCfg.tagLine      || 'Platform';
  const location = useLocation();
  const [open, setOpen] = useState(() => initOpenState(location));
  // VAP-BUG-FIX-COMPANY-MASTER-UX-1 §9 — narrow, opt-in fix for a systemic gap (logged, not
  // fixed globally): `open` only resyncs to the route when a caller explicitly asks for it via
  // navigate(to, { state: { syncSidebarSection: true } }) -- see directive §9.2 for why this
  // isn't unconditional for every navigation yet.
  useEffect(() => {
    if (location.state?.syncSidebarSection) setOpen(initOpenState(location));
  }, [location.pathname, location.state]);
  // SIDEBAR-SECTION-RESYNC (Brian ruling, 2026-10-02): unconditional resync -- `open` follows the
  // route on EVERY pathname change, not just opt-in navigations (the flag effect above is kept and
  // is now redundant, i.e. harmless). initOpenState() already strips `?tab=` per root CLAUDE.md
  // rule 21. Sections are an accordion (toggle() keeps one open), so the recompute opens exactly
  // the active section and closes the rest. ADMIN_GROUP is not in NAV_SECTIONS and initOpenState
  // hard-codes Admin:false, so keep Admin open only when it was open and no NAV section is active
  // (an Admin-link route) -- otherwise every click inside Admin would collapse it.
  useEffect(() => {
    setOpen((prev) => {
      const next = initOpenState(location);
      if (prev.Admin && !Object.values(next).some(Boolean)) next.Admin = true;
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  const { isMobile, isTablet } = useWindowWidth();
  const [railExpanded, setRailExpanded] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [navQuery,  setNavQuery]  = useState('');
  const [helpOpen, setHelpOpen] = useState(false);
  const [mobileFeedbackOpen, setMobileFeedbackOpen] = useState(false);
  const currentHelpModule = Object.entries(HELP_MODULE_MAP).find(([prefix]) =>
    location.pathname.startsWith(prefix))?.[1] ?? null;

  const { impersonation, endImpersonation } = useImpersonation();
  const { preview, endPreview }            = usePreview();
  const { isDark, toggleDark }             = useTheme();

  const [openAlertCount, setOpenAlertCount] = useState(0);
  const loadAlertCount = useCallback(async () => {
    try {
      const res = await api.get('/api/inventory/alerts');
      const alerts = Array.isArray(res.data) ? res.data : [];
      setOpenAlertCount(alerts.filter(a => a.Status === 'open').length);
    } catch { /* non-critical — badge just stays 0 */ }
  }, []);
  useEffect(() => { loadAlertCount(); }, [loadAlertCount]);

  // TARIFF-TONNAGE-REPORT-1 / PANJIVA-VOLUME-DB-1 — both nav rows are always
  // visible now (TENANT-MODULES-DECOMMISSION-1 removed the moduleGated check
  // this comment used to describe); LAYOUT-REPORT-PROBE-GATE-1's original
  // reason for avoiding a live probe fetch (2 extra 404s per page load on any
  // tenant without the NS-dev-only ImportTonnageMonthly/PanjivaShipmentVolume
  // tables) still applies, so no probe was added back in its place.

  // LEGAL-PAGE-AND-PROVISION-TEMPLATES-1 Part 3 — hide nav items whose backend
  // route is requireTenantTable-gated on a table this tenant's DB lacks.
  // caps === null while loading: gated items stay hidden until the signal lands.
  const caps = useCapabilities();
  const capOk = (link) => !link.cap || hasCapability(caps, link.cap);

  // NAV-SECTION-VISIBILITY-1 (2026-09-19, Brian ruling): a section's visibility is
  // computed from its own children, never a blanket section-level flag. TENANT-
  // MODULES-DECOMMISSION-1 (2026-09-28) removed the per-link moduleGated/isEnabled
  // check this comment used to describe — every link is now unconditionally
  // visible per roleOk/capOk/hidden, matching the platform-wide "no per-tenant
  // feature entitlement" ruling. Shared by the section filter, both render loops
  // (desktop + mobile already applied this same per-link check inline) and the
  // search-result section filter below.
  const linkVisible = (l) => !l.hidden && roleOk(l) && capOk(l);
  const sectionHasVisibleLink = (section) =>
    section.groups.some(g => g.links.some(l => linkVisible(l)));

  // RBAC-PM-GATE-CONSISTENCY-1: backend route is requireRole(['admin','qa_manager','qa_director'])
  // — this fetch used to fire unconditionally on every page load for every role, 403ing silently
  // for anyone outside that set (product_manager/sales were hitting it on every navigation).
  const [intakePendingCount, setIntakePendingCount] = useState(0);
  const canSeeIntakeBadge = ['admin', 'qa_manager', 'qa_director'].includes(role);
  const loadIntakeCount = useCallback(async () => {
    try {
      const res = await api.get('/api/vap/intake/pending/count');
      setIntakePendingCount(res.data?.count ?? 0);
    } catch { /* non-critical */ }
  }, []);
  useEffect(() => { if (canSeeIntakeBadge) loadIntakeCount(); }, [canSeeIntakeBadge, loadIntakeCount]);

  // ZEBRA-UX-1 — Label Issuance nav badge, same shape as the intake badge
  // above. Backend route is QA_ADMIN (admin/qa_manager) — matches who can
  // actually act on a pending request, same as canSeeIntakeBadge's own role
  // list narrowing to who the backend route actually allows.
  const [pendingLabelCount, setPendingLabelCount] = useState(0);
  const canSeeLabelBadge = ['admin', 'qa_manager'].includes(role);
  const loadPendingLabelCount = useCallback(async () => {
    try {
      const res = await api.get('/api/label-workflow/requests/pending-count');
      setPendingLabelCount(res.data?.count ?? 0);
    } catch { /* non-critical — badge just stays 0 */ }
  }, []);
  useEffect(() => { if (canSeeLabelBadge) loadPendingLabelCount(); }, [canSeeLabelBadge, loadPendingLabelCount]);

  // Nav sections are restricted during portal preview so the admin sees only
  // what that customer/vendor portal role would see.  Driven by preview context,
  // NOT the user's role, so regular auditor/commercial users are unaffected.
  const previewRole  = preview?.context?.previewRole;
  const baseNav       = previewRole === 'customer_portal' ? CUSTOMER_PORTAL_NAV
                     : previewRole === 'auditor'         ? VENDOR_PORTAL_NAV
                     : NAV_SECTIONS;
  // ROLE-SCOPED-NAV-1 — applied after preview resolution so it only ever
  // narrows the real NAV_SECTIONS list, never CUSTOMER_PORTAL_NAV/VENDOR_PORTAL_NAV.
  //
  // MOBILE-NAV-SEARCH-1 §1a fix (2026-09-27, Brian's ruling): `role` is
  // `undefined` while authState === 'loading', so `ROLE_SECTION_VISIBILITY
  // [undefined]` always missed and fell through to the unfiltered `baseNav`
  // -- a brief unrestricted-nav-labels flash on first load for exactly the
  // roles this map exists to restrict (warehouse/production). Gate on
  // authState instead of hoisting a shared /me call above ValidatedRoutes
  // (smallest blast radius): render no sections until the real role is
  // known, rather than every section.
  const roleSections  = ROLE_SECTION_VISIBILITY[role];
  const effectiveNav  = authState === 'loading'
    ? []
    : (roleSections
        ? baseNav.filter((section) => roleSections.includes(section.label))
        : baseNav
      ).filter((section) => sectionHasVisibleLink(section));

  // UX-1: Accordion behavior — only one section open at a time. Clicking
  // an open section collapses it; clicking a closed section opens that one
  // and closes any others.
  const toggle = (label) => setOpen((s) => {
    const willOpen = !s[label];
    const next = Object.fromEntries(Object.keys(s).map(k => [k, false]));
    next[label] = willOpen;
    return next;
  });
  const closeMore = () => setMoreOpen(false);

  const moreActive = !PRIMARY_TAB_PATHS.some((p) => location.pathname.startsWith(p));

  // Clear search on route change
  useEffect(() => { setNavQuery(''); }, [location.pathname]);

  // Autofill guard: a stray '@' means a password manager / browser injected
  // a saved email into this field, not a real search query (BUG-AUTOFILL-SWEEP-2)
  useEffect(() => {
    if (navQuery.includes('@')) {
      if (import.meta.env.DEV) console.warn('[nav search] cleared injected value containing "@"');
      setNavQuery('');
    }
  }, [navQuery]);

  // HR-ONBOARD-1 Phase 1 §2 — a new hire (Users.Role='onboarding') sees no
  // portal chrome at all, not even a filtered nav; every route under Layout
  // bounces straight to the full-screen wizard (declared outside Layout in
  // App.jsx). Placed after every hook call in this component (Rules of Hooks
  // — an early return before all hooks run would change hook-call order
  // across renders). Gated on authState so this doesn't fire on the one-tick
  // "not yet known" render before /api/users/me resolves. The backend's
  // onboardingScopeGuard.js is the real security boundary — this is UX only.
  if (authState !== 'loading' && role === 'onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  // Build flat search results when query is active
  const q = navQuery.trim().toLowerCase();
  let searchResults = null;
  if (q) {
    searchResults = [];
    // NAV-SEARCH-DEDUP-1 (2026-09-19, Brian): TAB_SEARCH_ENTRIES does double duty as
    // the search index AND the Playwright tab-variant source (extract-routes.mjs), so
    // a row can't just be deleted when it duplicates a plain NAV_SECTIONS link -- doing
    // so silently drops that tab from Playwright's coverage (see e.g. Batch Records'
    // ?tab=records, which shares its base with the genuinely distinct ?tab=room-logs
    // row). Instead, dedupe at render time: several TAB_SEARCH_ENTRIES rows exist that
    // point at the exact same destination (or the same default-tab destination under a
    // different query string) as an already-searchable plain link -- e.g. "Recall &
    // Mock Drill" under SAFETY showed twice for "moc" (once via the normal NAV_SECTIONS
    // loop, once via its own redundant TAB_SEARCH_ENTRIES row). Same-label results
    // within one section are always genuine duplicates in this codebase (verified: no
    // two TAB_SEARCH_ENTRIES rows share a label), so first-match-wins by
    // sectionLabel+label is a safe general fix, not a per-row patch.
    const seenResultKeys = new Set();
    function pushResult(sectionLabel, link) {
      const key = `${sectionLabel}::${link.label}`;
      if (seenResultKeys.has(key)) return;
      seenResultKeys.add(key);
      searchResults.push({ sectionLabel, link });
    }
    if ('dashboard'.includes(q)) {
      pushResult(null, { to: '/dashboard', label: 'Dashboard' });
    }
    for (const section of effectiveNav) {
      for (const group of section.groups) {
        for (const link of group.links) {
          if (linkVisible(link) && link.label.toLowerCase().includes(q)) {
            pushResult(section.label, link);
          }
        }
      }
    }
    // ROLE-SCOPED-NAV-1 — ING_SECTION rides on SALES (see effectiveNav render
    // loop below); a role restricted away from SALES must not surface it via search either.
    if (!roleSections || roleSections.includes('SALES')) {
      for (const group of ING_SECTION.groups) {
        for (const link of group.links) {
          if (capOk(link) && link.label.toLowerCase().includes(q)) {
            pushResult('INGREDIENT MGMT', link);
          }
        }
      }
    }
    for (const link of ADMIN_GROUP.links) {
      if (roleOk(link) && capOk(link) && link.label.toLowerCase().includes(q)) {
        pushResult('ADMIN', link);
      }
    }
    // Tab-level entries for merged hub shells
    const tenantSlug = _tenantCfg.tenantSlug || '';
    for (const entry of TAB_SEARCH_ENTRIES) {
      if (entry.sustainabilityOnly && tenantSlug !== 'rochem') continue;
      if (!roleOk(entry)) continue;
      if (roleSections && !roleSections.includes(entry.sectionLabel)) continue;
      if (!capOk(entry.link)) continue;
      const labelMatch = entry.link.label.toLowerCase().includes(q);
      const kwMatch    = entry.keywords.some(k => k.includes(q));
      if (labelMatch || kwMatch) {
        pushResult(entry.sectionLabel, entry.link);
      }
    }
  }

  /* ── Shared: render one nav section ────────────────────────────────── */
  function renderSection(section, onLinkClick) {
    // Only show group sub-labels when the section has 2+ groups.
    // Single-group sections (Production, Compliance, Finance) skip the label
    // so it doesn't just repeat the section header.
    const showGroupLabels = section.groups.length > 1;
    return (
      <div key={section.label}>
        <button
          className="ns-section-btn"
          onClick={() => toggle(section.label)}
          aria-expanded={open[section.label]}
        >
          <span>{section.label}</span>
          <span
            className="ns-chevron"
            style={{ transform: open[section.label] ? 'rotate(90deg)' : 'rotate(0deg)' }}
          >
            ▶
          </span>
        </button>
        {open[section.label] && (
          <div>
            {section.groups.map((group) => (
              <div key={group.label}>
                {showGroupLabels && <div style={groupLabelStyle}>{group.label}</div>}
                {group.links.filter(l => linkVisible(l)).map((link) => (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    onClick={onLinkClick}
                    className={({ isActive }) => 'ns-nav-link' + (isActive ? ' active' : '')}
                    style={{ paddingLeft: showGroupLabels ? '1.375rem' : '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <span>{link.label}</span>
                    {link.alertBadge && openAlertCount > 0 && (
                      <span style={{ background: 'var(--color-danger)', color: 'var(--color-accent-btn-text)', borderRadius: 8, padding: '1px 6px', fontSize: 10, fontWeight: 700, lineHeight: 1.4 }}>
                        {openAlertCount}
                      </span>
                    )}
                    {link.intakeBadge && intakePendingCount > 0 && (
                      <span style={{ background: 'var(--color-warning-text)', color: 'var(--color-accent-btn-text)', borderRadius: 8, padding: '1px 6px', fontSize: 10, fontWeight: 700, lineHeight: 1.4 }}>
                        {intakePendingCount}
                      </span>
                    )}
                    {link.pendingLabelBadge && pendingLabelCount > 0 && (
                      <span style={{ background: 'var(--color-warning-text)', color: 'var(--color-accent-btn-text)', borderRadius: 8, padding: '1px 6px', fontSize: 10, fontWeight: 700, lineHeight: 1.4 }}>
                        {pendingLabelCount}
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  /* ── Shared: render the Admin group (collapsible, default collapsed) ──
     Shown to anyone who can see at least one link inside it (usually
     isAdmin, but an hr_admin-only account sees it too, scoped to just the
     HR link via roleOk() below) — not a bare isAdmin check, so HR-ONBOARD-1's
     folded-in link stays reachable for that role. Shared by desktop (fullNav)
     and mobile (More drawer, MOBILE-NAV-ACCORDION-1) so both use the exact
     same open/toggle state instead of two independently-coded renderings. */
  function renderAdminGroup(onLinkClick) {
    if (!ADMIN_GROUP.links.some(roleOk)) return null;
    return (
      <div key="Admin">
        <button
          className="ns-section-btn"
          onClick={() => toggle('Admin')}
          aria-expanded={open['Admin']}
        >
          <span>ADMIN</span>
          <span
            className="ns-chevron"
            style={{ transform: open['Admin'] ? 'rotate(90deg)' : 'rotate(0deg)' }}
          >
            ▶
          </span>
        </button>
        {open['Admin'] && (
          <div>
            {ADMIN_GROUP.links.filter(roleOk).map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                onClick={onLinkClick}
                className={({ isActive }) => 'ns-nav-link' + (isActive ? ' active' : '')}
                style={{ paddingLeft: '1rem' }}
              >
                {link.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  }

  /* ── Full sidebar nav ───────────────────────────────────────────────── */
  const fullNav = (
    <nav style={{ flex: 1, overflowY: 'auto', paddingBottom: '0.5rem' }}>
      <NavLink
        to="/dashboard"
        className={({ isActive }) => 'ns-nav-link ns-nav-link--top' + (isActive ? ' active' : '')}
      >
        Dashboard
      </NavLink>

      {/* Render order: PROFILES → QUALITY → PRODUCTION → SALES → INGREDIENT MGMT → FINANCE → SAFETY → DOCUMENT CONTROL */}
      {(() => {
        const out = [];
        for (const section of effectiveNav) {
          out.push(renderSection(section, undefined));
          if (section.label === 'SALES') {
            out.push(renderSection(ING_SECTION, undefined));
          }
        }
        return out;
      })()}

      {renderAdminGroup(undefined)}

    </nav>
  );

  /* ── Search results nav ─────────────────────────────────────────────── */
  // Fail open, not closed (BUG-AUTOFILL-SWEEP-2): a zero-match query — whether
  // typed, pasted, or autofill-injected — must never hide the nav. Show the
  // "No results" line, then fall through to the full nav below it.
  const searchNav = (
    <nav style={{ flex: 1, overflowY: 'auto', paddingBottom: '0.5rem' }}>
      {searchResults && searchResults.length === 0 && (
        <div style={{ padding: '0.75rem 1rem 0.25rem', fontSize: '0.72rem', color: 'rgba(248,243,236,0.35)', textAlign: 'center' }}>
          No results for &quot;{navQuery}&quot;
        </div>
      )}
      {searchResults && searchResults.length === 0 ? (
        fullNav
      ) : (
        searchResults && searchResults.map(({ sectionLabel, link }, i) => (
          <div key={link.to + i}>
            {sectionLabel && (
              <div style={{
                padding:       '0.375rem 1rem 0.1rem',
                fontSize:      '0.555rem',
                fontWeight:    700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color:         'rgba(95,202,201,0.55)',
                marginTop:     i > 0 ? '0.25rem' : 0,
              }}>
                {sectionLabel}
              </div>
            )}
            {link.external ? (
              <a
                href={link.to}
                target="_blank"
                rel="noopener noreferrer"
                className="ns-nav-link"
                style={{ paddingLeft: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                {link.label}
              </a>
            ) : (
              <NavLink
                to={link.to}
                className={({ isActive }) => 'ns-nav-link' + (isActive ? ' active' : '')}
                style={{ paddingLeft: '1rem' }}
              >
                {link.label}
              </NavLink>
            )}
          </div>
        ))
      )}
    </nav>
  );

  /* ── Tablet icon rail ───────────────────────────────────────────────── */
  const iconRail = (
    <nav style={{ flex: 1, overflowY: 'auto', paddingTop: '0.25rem', paddingBottom: '0.5rem' }}>
      <NavLink
        to="/dashboard"
        title="Dashboard"
        style={({ isActive }) => ({
          ...railIconLink,
          background: isActive ? 'rgba(13,113,128,0.38)' : 'transparent',
          color: isActive ? 'var(--color-bg-primary)' : 'rgba(248,243,236,0.65)',
        })}
      >
        <DashIcon />
      </NavLink>
      {effectiveNav.map((section) => {
        const allLinks = section.groups.flatMap(g => g.links);
        const firstTo  = allLinks[0]?.to ?? '/dashboard';
        const isActive = allLinks.some(l => location.pathname.startsWith(l.to));
        return (
          <NavLink
            key={section.label}
            to={firstTo}
            title={section.label}
            style={{
              ...railIconLink,
              background: isActive ? 'rgba(13,113,128,0.38)' : 'transparent',
              color: isActive ? 'var(--color-bg-primary)' : 'rgba(248,243,236,0.65)',
            }}
          >
            <span style={railTextCode}>{section.label.slice(0, 4).toUpperCase()}</span>
          </NavLink>
        );
      })}
    </nav>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', fontFamily: 'system-ui, sans-serif' }}>

      {/* ── Impersonation banner ─────────────────────────────────────────── */}
      {impersonation && (
        <div style={{
          position:       'sticky',
          top:            0,
          zIndex:         1000,
          background:     'var(--color-border)',
          borderBottom:   '2px solid var(--color-archive)',
          padding:        '8px 20px',
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'space-between',
          fontSize:       '13px',
          fontWeight:     600,
          color: 'var(--color-text-primary)',
          flexShrink:     0,
        }}>
          <span>
            Viewing as {impersonation.impersonatedUser?.name}
            {' '}({impersonation.impersonatedUser?.role}) —{' '}
            you are seeing exactly what they see
          </span>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.7 }}>
              Session expires in{' '}
              <ImpersonationTimer
                startedAt={impersonation.startedAt}
                onExpire={endImpersonation}
              />
            </span>
            <button className="ns-btn ns-btn-primary ns-btn-sm" onClick={() => endImpersonation('manual')}>
              Exit View As
            </button>
          </div>
        </div>
      )}

      {/* ── Portal preview banner ───────────────────────────────────────── */}
      {preview && (
        <div style={{
          position:       'sticky',
          top:            0,
          zIndex:         1000,
          background:     'var(--color-border)',
          borderBottom:   '2px solid var(--color-archive)',
          padding:        '8px 20px',
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'space-between',
          fontSize:       '13px',
          fontWeight:     600,
          color: 'var(--color-text-primary)',
          flexShrink:     0,
        }}>
          <span>
            Previewing as: <strong>{preview.context.previewEntityName}</strong>
            {' — '}
            {preview.context.previewRole === 'customer_portal' ? 'Customer Portal' : 'Vendor Portal'}
            {' — '}
            you see only their data
          </span>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.7 }}>
              Session expires in{' '}
              <ImpersonationTimer
                startedAt={preview.startedAt}
                onExpire={endPreview}
              />
            </span>
            <button className="ns-btn ns-btn-primary ns-btn-sm" onClick={() => endPreview('manual')}>
              Exit Preview
            </button>
          </div>
        </div>
      )}

      {/* ── Layout row (sidebar + main) ─────────────────────────────────── */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

      {/* ── Sidebar (tablet + desktop) ──────────────────────────────────── */}
      {!isMobile && (
        <aside style={{
          ...sidebar,
          width:    isTablet ? (railExpanded ? '240px' : '56px') : '220px',
          minWidth: isTablet ? (railExpanded ? '240px' : '56px') : '220px',
          transition: 'width 0.2s ease, min-width 0.2s ease',
          overflow: 'hidden',
        }}>

          {isTablet && (
            <button
              onClick={() => setRailExpanded((v) => !v)}
              style={railToggleBtn}
              aria-label={railExpanded ? 'Collapse navigation' : 'Expand navigation'}
            >
              {railExpanded ? '✕' : <HamburgerIcon />}
            </button>
          )}

          {(!isTablet || railExpanded) && (
            <div style={logoArea}>
              <div style={{ fontWeight: 800, fontSize: '1.125rem', color: 'var(--color-bg-primary)', letterSpacing: '-0.01em' }}>
                {brandName}
              </div>
              <div style={{ fontSize: '0.65rem', color: 'var(--color-accent)', marginTop: '0.125rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                {brandTagline}
              </div>
            </div>
          )}

          {(!isTablet || railExpanded) && (
            <div style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid rgba(255,255,255,0.08)', position: 'relative' }}>
              <svg
                width="13" height="13" viewBox="0 0 16 16" fill="none"
                style={{ position: 'absolute', left: '1.2rem', top: '50%', transform: 'translateY(-50%)', color: 'rgba(248,243,236,0.35)', pointerEvents: 'none' }}
              >
                <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <input
                type="text"
                name="nsq"
                id="nsq"
                placeholder="Search navigation…"
                value={navQuery}
                onChange={e => setNavQuery(e.target.value)}
                onKeyDown={e => { if (e.key === 'Escape') setNavQuery(''); }}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                data-lpignore="true"
                data-1p-ignore=""
                data-form-type="other"
                data-bwignore=""
                style={{
                  width:        '100%',
                  background:   'rgba(255,255,255,0.07)',
                  border:       '1px solid rgba(255,255,255,0.12)',
                  borderRadius: '0.25rem',
                  padding:      '0.35rem 1.5rem 0.35rem 1.875rem',
                  fontSize:     '0.72rem',
                  color:        'var(--color-bg-primary)',
                  outline:      'none',
                  boxSizing:    'border-box',
                }}
              />
              {navQuery && (
                <button
                  onClick={() => setNavQuery('')}
                  aria-label="Clear search"
                  style={{
                    position:   'absolute',
                    right:      '1.2rem',
                    top:        '50%',
                    transform:  'translateY(-50%)',
                    background: 'none',
                    border:     'none',
                    cursor:     'pointer',
                    color:      'rgba(248,243,236,0.45)',
                    fontSize:   '0.7rem',
                    padding:    '0 2px',
                    lineHeight: 1,
                  }}
                >✕</button>
              )}
            </div>
          )}

          {isTablet && !railExpanded ? iconRail : (searchResults ? searchNav : fullNav)}

          {(!isTablet || railExpanded) && (
            <div style={userBar}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <div style={{ fontSize: '0.7rem', color: 'var(--ns-blue-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                  {user?.name ?? user?.username ?? dbName ?? 'Not signed in'}
                </div>
                <NotificationBell />
                <button
                  onClick={() => setHelpOpen(true)}
                  title="Help"
                  aria-label="Help"
                  style={{ background: 'none', border: '1px solid var(--color-accent)', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', color: 'var(--color-accent)', fontSize: '0.7rem', fontWeight: 700, lineHeight: 1, padding: 0, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  ?
                </button>
                <button
                  onClick={toggleDark}
                  title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(248,243,236,0.65)', fontSize: '0.875rem', padding: '2px 4px', lineHeight: 1, flexShrink: 0 }}
                >
                  {isDark ? '☀' : '☾'}
                </button>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => navigate('/settings')} style={{ ...logoutBtn, background: 'transparent', color: 'var(--ns-blue-1)', border: '1px solid var(--color-border-neutral)' }}>
                  My Settings
                </button>
                <button onClick={handleSignOut} style={logoutBtn}>
                  Sign out
                </button>
              </div>
            </div>
          )}
        </aside>
      )}

      {/* ── Main content ─────────────────────────────────────────────────── */}
      <main style={{
        ...mainArea,
        padding: isMobile ? '1rem 1rem 80px' : isTablet ? '1.5rem 1.75rem' : '2rem 2.25rem',
      }}>
        <ContentErrorBoundary>
          <Outlet />
        </ContentErrorBoundary>
      </main>

      </div>{/* end layout row */}

      {/* ── Bottom tab bar (mobile) ──────────────────────────────────────── */}
      {isMobile && (
        <nav style={bottomTabBar} aria-label="Main navigation">
          <MobileTab to="/dashboard"      icon={<DashIcon />}      label="Dashboard" />
          <MobileTab to="/goods-receipts" icon={<ReceivingIcon />} label="Receiving" />
          <MobileTab to="/inventory-lots" icon={<QualIcon />}      label="QMS"       />
          <MobileTab to="/documents"      icon={<DocsIcon />}      label="Docs"      />
          <button
            onClick={() => setMoreOpen(true)}
            style={{
              ...mobileTabBtn,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: moreActive ? 'var(--color-accent)' : 'rgba(248,243,236,0.6)',
            }}
            aria-label="More navigation options"
          >
            <MoreDotsIcon />
            <span style={tabLabelStyle}>More</span>
          </button>
        </nav>
      )}

      {/* ── "More" drawer (mobile) ───────────────────────────────────────── */}
      {isMobile && moreOpen && (
        <>
          <div style={moreBackdrop} onClick={closeMore} aria-hidden="true" />
          <div style={moreDrawer} role="dialog" aria-modal="true" aria-label="Navigation menu">
            <div style={moreDrawerHeader}>
              <span style={{ fontWeight: 700, fontSize: '0.875rem', color: 'var(--color-bg-primary)', letterSpacing: '0.02em' }}>
                {brandName} Menu
              </span>
              <button onClick={closeMore} style={moreCloseBtn} aria-label="Close menu">✕</button>
            </div>

            <div style={{ padding: '0.625rem 1rem 0.5rem', borderBottom: '1px solid rgba(255,255,255,0.08)', fontSize: '0.7rem', color: 'var(--ns-blue-1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                {user?.name ?? user?.username ?? 'Not signed in'}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <NotificationBell />
                <button
                  onClick={toggleDark}
                  title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(248,243,236,0.65)', fontSize: '0.875rem', padding: '2px 6px', lineHeight: 1, flexShrink: 0 }}
                >
                  {isDark ? '☀' : '☾'}
                </button>
              </div>
            </div>

            <div style={{ padding: '0.625rem 1rem', borderBottom: '1px solid rgba(255,255,255,0.08)', position: 'relative' }}>
              <svg
                width="13" height="13" viewBox="0 0 16 16" fill="none"
                style={{ position: 'absolute', left: '1.5rem', top: '50%', transform: 'translateY(-50%)', color: 'rgba(248,243,236,0.35)', pointerEvents: 'none' }}
              >
                <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <input
                type="text"
                name="nsq-mobile"
                id="nsq-mobile"
                placeholder="Search navigation…"
                value={navQuery}
                onChange={e => setNavQuery(e.target.value)}
                onKeyDown={e => { if (e.key === 'Escape') setNavQuery(''); }}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                data-lpignore="true"
                data-1p-ignore=""
                data-form-type="other"
                data-bwignore=""
                style={{
                  width:        '100%',
                  background:   'rgba(255,255,255,0.07)',
                  border:       '1px solid rgba(255,255,255,0.12)',
                  borderRadius: '0.25rem',
                  padding:      '0.5rem 1.75rem 0.5rem 2.125rem',
                  fontSize:     '0.8rem',
                  color:        'var(--color-bg-primary)',
                  outline:      'none',
                  boxSizing:    'border-box',
                }}
              />
              {navQuery && (
                <button
                  onClick={() => setNavQuery('')}
                  aria-label="Clear search"
                  style={{
                    position:   'absolute',
                    right:      '1.5rem',
                    top:        '50%',
                    transform:  'translateY(-50%)',
                    background: 'none',
                    border:     'none',
                    cursor:     'pointer',
                    color:      'rgba(248,243,236,0.45)',
                    fontSize:   '0.75rem',
                    padding:    '0 2px',
                    lineHeight: 1,
                  }}
                >✕</button>
              )}
            </div>

            <nav style={{ flex: 1, overflowY: 'auto' }}>
              {searchResults && searchResults.length === 0 && (
                <div style={{ padding: '0.75rem 1rem 0.25rem', fontSize: '0.72rem', color: 'rgba(248,243,236,0.35)', textAlign: 'center' }}>
                  No results for &quot;{navQuery}&quot;
                </div>
              )}
              {searchResults && searchResults.length > 0 ? (
                searchResults.map(({ sectionLabel, link }, i) => (
                  <div key={link.to + i}>
                    {sectionLabel && (
                      <div style={moreDrawerSectionLabel}>{sectionLabel}</div>
                    )}
                    {link.external ? (
                      <a
                        href={link.to}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={closeMore}
                        className="ns-nav-link"
                        style={{ paddingLeft: '1rem' }}
                      >
                        {link.label}
                      </a>
                    ) : (
                      <NavLink
                        to={link.to}
                        onClick={closeMore}
                        className={({ isActive }) => 'ns-nav-link' + (isActive ? ' active' : '')}
                        style={{ paddingLeft: '1rem' }}
                      >
                        {link.label}
                      </NavLink>
                    )}
                  </div>
                ))
              ) : (
                <>
                  <NavLink
                    to="/dashboard"
                    onClick={closeMore}
                    className={({ isActive }) => 'ns-nav-link ns-nav-link--top' + (isActive ? ' active' : '')}
                  >
                    Dashboard
                  </NavLink>

                  {/* MOBILE-NAV-ACCORDION-1 — same renderSection()/renderAdminGroup()
                      shared with the desktop rail (same open/toggle state, same
                      accordion-one-at-a-time behavior, same active-route-opens-on-load
                      default via initOpenState) instead of a second, always-expanded
                      rendering. Render order matches fullNav's own comment above. */}
                  {(() => {
                    const out = [];
                    for (const section of effectiveNav) {
                      out.push(renderSection(section, closeMore));
                      if (section.label === 'SALES') {
                        out.push(renderSection(ING_SECTION, closeMore));
                      }
                    }
                    return out;
                  })()}

                  {renderAdminGroup(closeMore)}
                </>
              )}
            </nav>

            <div style={{ padding: '0.875rem 1rem', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <button
                onClick={() => { setHelpOpen(true); closeMore(); }}
                style={{ ...logoutBtn, width: '100%', justifyContent: 'center', padding: '0.625rem 0', background: 'transparent', color: 'var(--ns-blue-1)', border: '1px solid var(--color-border-neutral)' }}
              >
                Help
              </button>
              <button
                onClick={() => { setMobileFeedbackOpen(true); closeMore(); }}
                style={{ ...logoutBtn, width: '100%', justifyContent: 'center', padding: '0.625rem 0', background: 'transparent', color: 'var(--ns-blue-1)', border: '1px solid var(--color-border-neutral)' }}
              >
                Send feedback
              </button>
              <button
                onClick={() => { navigate('/settings'); closeMore(); }}
                style={{ ...logoutBtn, width: '100%', justifyContent: 'center', padding: '0.625rem 0', background: 'transparent', color: 'var(--ns-blue-1)', border: '1px solid var(--color-border-neutral)' }}
              >
                My Settings
              </button>
              <button
                onClick={handleSignOut}
                style={{ ...logoutBtn, width: '100%', justifyContent: 'center', padding: '0.625rem 0' }}
              >
                Sign out
              </button>
            </div>
          </div>
        </>
      )}

      <HelpDrawer isOpen={helpOpen} onClose={() => setHelpOpen(false)} initialModule={currentHelpModule} />
      {/* HELPBOT-LAUNCHER-MOBILE-NAV-COLLISION-1: the fixed bottom-right bubble sits
          directly on top of the mobile bottom-tab-bar's "More" tab -- desktop/tablet only.
          FEEDBACK-MOBILE-ENTRY-1: mobile's own entry point is the "Send feedback" row in
          the More sheet above, which opens this same FeedbackModal directly. */}
      {!isMobile && <FeedbackButton />}
      {isMobile && mobileFeedbackOpen && (
        <FeedbackModal onClose={() => setMobileFeedbackOpen(false)} />
      )}
    </div>
  );
}

/* ── Style objects ───────────────────────────────────────────────────────── */

const sidebar = {
  background:    'var(--color-bg-sidebar)',
  display:       'flex',
  flexDirection: 'column',
  boxShadow:     '2px 0 12px rgba(6,39,34,0.35)',
  flexShrink:    0,
};

const logoArea = {
  padding:      '1.25rem 1rem 1.125rem',
  borderBottom: '1px solid rgba(255,255,255,0.08)',
};

const mainArea = {
  flex:       1,
  overflowY:  'auto',
  background: 'var(--color-bg-primary, var(--color-bg-primary))',
  minWidth:   0,
};

const userBar = {
  padding:       '0.75rem 1rem',
  borderTop:     '1px solid rgba(255,255,255,0.08)',
  background:    'var(--color-bg-topbar, var(--color-text-secondary))',
  display:       'flex',
  flexDirection: 'column',
  gap:           '0.5rem',
};

const logoutBtn = {
  padding:       '0.3rem 0.75rem',
  fontSize:      '0.7rem',
  fontWeight:    600,
  border:        '1px solid rgba(95,202,201,0.4)',
  borderRadius:  '0.25rem',
  background:    'transparent',
  cursor:        'pointer',
  color:         'var(--color-accent)',
  letterSpacing: '0.04em',
  alignSelf:     'flex-start',
  display:       'flex',
};

const railToggleBtn = {
  display:        'flex',
  alignItems:     'center',
  justifyContent: 'center',
  width:          '56px',
  height:         '52px',
  background:     'transparent',
  border:         'none',
  cursor:         'pointer',
  color:          'rgba(248,243,236,0.8)',
  fontSize:       '1.125rem',
  flexShrink:     0,
  borderBottom:   '1px solid rgba(255,255,255,0.06)',
};

const railIconLink = {
  display:        'flex',
  alignItems:     'center',
  justifyContent: 'center',
  width:          '56px',
  height:         '48px',
  borderLeft:     '2px solid transparent',
  transition:     'background 0.12s, color 0.12s',
  textDecoration: 'none',
};

// Collapsed rail section codes (SIDEBAR-RAIL-TEXT-1) — first 4 letters of
// section.label, uppercase, replacing the icon glyph. Sizing/highlight
// treatment is inherited unchanged from railIconLink on the parent NavLink.
const railTextCode = {
  fontWeight:    700,
  fontSize:      '0.65rem',
  letterSpacing: '0.04em',
  textAlign:     'center',
};

const groupLabelStyle = {
  padding:       '0.375rem 0.75rem 0.2rem 1rem',
  fontSize:      '0.555rem',
  fontWeight:    700,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color:         'rgba(95,202,201,0.55)',
  marginTop:     '0.25rem',
};

const bottomTabBar = {
  position:       'fixed',
  bottom:         0,
  left:           0,
  right:          0,
  height:         '64px',
  background:     'var(--color-bg-sidebar)',
  borderTop:      '1px solid rgba(255,255,255,0.1)',
  display:        'flex',
  alignItems:     'stretch',
  zIndex:         900,
  boxShadow:      '0 -2px 12px rgba(6,39,34,0.35)',
};

const mobileTabBtn = {
  flex:           1,
  display:        'flex',
  flexDirection:  'column',
  alignItems:     'center',
  justifyContent: 'center',
  gap:            '2px',
  fontSize:       '0.6rem',
  fontWeight:     600,
  letterSpacing:  '0.02em',
  textDecoration: 'none',
  padding:        '8px 4px',
};

const tabLabelStyle = {
  fontSize:    '0.6rem',
  fontWeight:  600,
  letterSpacing: '0.03em',
  lineHeight:  1,
};

const moreBackdrop = {
  position:   'fixed',
  inset:      0,
  background: 'rgba(6,39,34,0.6)',
  zIndex:     950,
};

const moreDrawer = {
  position:      'fixed',
  bottom:        0,
  left:          0,
  right:         0,
  maxHeight:     '85vh',
  background:    'var(--color-bg-sidebar)',
  borderRadius:  '1rem 1rem 0 0',
  display:       'flex',
  flexDirection: 'column',
  zIndex:        960,
  boxShadow:     '0 -8px 32px rgba(6,39,34,0.5)',
};

const moreDrawerHeader = {
  display:        'flex',
  alignItems:     'center',
  justifyContent: 'space-between',
  padding:        '1rem 1rem 0.75rem',
  borderBottom:   '1px solid rgba(255,255,255,0.08)',
  flexShrink:     0,
};

const moreCloseBtn = {
  width:          '2rem',
  height:         '2rem',
  display:        'flex',
  alignItems:     'center',
  justifyContent: 'center',
  background:     'rgba(255,255,255,0.08)',
  border:         '1px solid rgba(255,255,255,0.15)',
  borderRadius:   '50%',
  cursor:         'pointer',
  color:          'var(--color-bg-primary)',
  fontSize:       '0.875rem',
};

const moreDrawerSectionLabel = {
  padding:       '0.5rem 0.875rem 0.25rem',
  fontSize:      '0.575rem',
  fontWeight:    700,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color:         'rgba(95,202,201,0.9)',
  background:    'var(--color-text-secondary)',
  borderBottom:  '1px solid rgba(255,255,255,0.06)',
};

